import { getRng } from '../utils/Random';

export interface Relic {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'boss';
  // Effects are checked by ID in game logic
}

const RELIC_POOL: Relic[] = [
  // Common relics
  {
    id: 'iron_heart',
    name: 'Iron Heart',
    description: '+5 max HP',
    rarity: 'common',
  },
  {
    id: 'lucky_coin',
    name: 'Lucky Coin',
    description: 'Start each combat with 3 block',
    rarity: 'common',
  },
  {
    id: 'whetstone',
    name: 'Whetstone',
    description: '+1 damage to all attacks',
    rarity: 'common',
  },
  {
    id: 'leather_shield',
    name: 'Leather Shield',
    description: '+1 block from all defense cards',
    rarity: 'common',
  },
  {
    id: 'healing_herb',
    name: 'Healing Herb',
    description: 'Heal 3 HP after each combat',
    rarity: 'common',
  },
  {
    id: 'battle_standard',
    name: 'Battle Standard',
    description: 'Positive number cards get +1 attack and +1 block',
    rarity: 'common',
  },
  {
    id: 'tactical_manual',
    name: 'Tactical Manual',
    description: 'Turn 1: ATK zone cap +1',
    rarity: 'common',
  },
  {
    id: 'guard_manual',
    name: 'Guard Manual',
    description: 'Turn 1: DEF zone cap +1',
    rarity: 'common',
  },
  {
    id: 'focus_manual',
    name: 'Focus Manual',
    description: 'Turn 1: ABL zone cap +1',
    rarity: 'common',
  },
  {
    id: 'ember_totem',
    name: 'Ember Totem',
    description: 'Turn 1: draw +1 card',
    rarity: 'common',
  },

  // Uncommon relics
  {
    id: 'combo_ring',
    name: 'Combo Ring',
    description: 'Combo bonuses are doubled',
    rarity: 'uncommon',
  },
  {
    id: 'glass_cannon',
    name: 'Glass Cannon',
    description: '+3 damage to attacks. Take +1 damage from enemies',
    rarity: 'uncommon',
  },
  {
    id: 'mirror_shard',
    name: 'Mirror Shard',
    description: 'Negative cards deal 2 extra ability damage',
    rarity: 'uncommon',
  },
  {
    id: 'thick_skin',
    name: 'Thick Skin',
    description: 'Block from defense cards cannot go below 1',
    rarity: 'uncommon',
  },
  {
    id: 'card_magnet',
    name: 'Card Magnet',
    description: 'Draw 1 extra card on turn 1 of each combat',
    rarity: 'uncommon',
  },
  {
    id: 'burning_blood',
    name: 'Burning Blood',
    description: 'Heal 6 HP at end of each combat',
    rarity: 'uncommon',
  },
  {
    id: 'hunter_mark',
    name: 'Hunter Mark',
    description: 'Attack cards 8+ get +2 damage',
    rarity: 'uncommon',
  },
  {
    id: 'bulwark_emblem',
    name: 'Bulwark Emblem',
    description: 'Defense cards 1-3 get +2 block',
    rarity: 'uncommon',
  },
  {
    id: 'arcane_lens',
    name: 'Arcane Lens',
    description: 'Ability modifier effects gain +1',
    rarity: 'uncommon',
  },
  {
    id: 'curse_anvil',
    name: 'Curse Anvil',
    description: 'Playing a curse in ABL gives draw +1 next turn',
    rarity: 'uncommon',
  },

  // Rare relics
  {
    id: 'perfect_lens',
    name: 'Perfect Lens',
    description: 'Perfect 10 combo also heals 5 HP',
    rarity: 'rare',
  },
  {
    id: 'number_crown',
    name: 'Number Crown',
    description: 'Cards 1-3 get +2 attack. Cards 8-10 get +2 block',
    rarity: 'rare',
  },
  {
    id: 'infinity_loop',
    name: 'Infinity Loop',
    description: 'Negative cards in attack zone deal damage instead of healing enemy',
    rarity: 'rare',
  },
  {
    id: 'tempo_core',
    name: 'Tempo Core',
    description: 'Draw +1 card every turn',
    rarity: 'rare',
  },
  {
    id: 'stance_compass',
    name: 'Stance Compass',
    description: 'Vs CAST: attacks +3. Vs AGGRO: defense +2',
    rarity: 'rare',
  },
  {
    id: 'combo_archive',
    name: 'Combo Archive',
    description: 'Whenever you trigger any combo, draw +1 next turn',
    rarity: 'rare',
  },

  // Boss relics (one offered after boss kill)
  {
    id: 'abacus_bead',
    name: 'Abacus Bead',
    description: '+2 to all modifier effects',
    rarity: 'boss',
  },
  {
    id: 'digital_crown',
    name: 'Digital Crown',
    description: 'Start each combat with 1 Strength',
    rarity: 'boss',
  },
  {
    id: 'null_stone',
    name: 'Null Stone',
    description: 'Negative cards count as positive for combos',
    rarity: 'boss',
  },
  {
    id: 'triune_matrix',
    name: 'Triune Matrix',
    description: 'All zone caps +1 every turn',
    rarity: 'boss',
  },
  {
    id: 'echo_engine',
    name: 'Echo Engine',
    description: 'Each combo grants +2 damage and +2 block',
    rarity: 'boss',
  },
];

export function getRelicById(id: string): Relic | undefined {
  return RELIC_POOL.find(r => r.id === id);
}

export function generateRelicReward(
  rarity: 'common' | 'uncommon' | 'rare' | 'boss',
  ownedRelicIds: string[],
): Relic | null {
  const rng = getRng();
  const available = RELIC_POOL.filter(r =>
    r.rarity === rarity && !ownedRelicIds.includes(r.id)
  );
  if (available.length === 0) return null;
  return rng.pick(available);
}

export function generateBossRelicChoice(ownedRelicIds: string[]): Relic[] {
  const rng = getRng();
  const available = RELIC_POOL.filter(r =>
    r.rarity === 'boss' && !ownedRelicIds.includes(r.id)
  );
  return rng.shuffle([...available]).slice(0, Math.min(3, available.length));
}

export function generateEliteRelicReward(ownedRelicIds: string[]): Relic | null {
  const rng = getRng();
  // Elites drop uncommon or rare
  const pool = RELIC_POOL.filter(r =>
    (r.rarity === 'uncommon' || r.rarity === 'rare') &&
    !ownedRelicIds.includes(r.id)
  );
  if (pool.length === 0) return null;
  // 70% uncommon, 30% rare
  const targetRarity = rng.int(0, 9) < 7 ? 'uncommon' : 'rare';
  const filtered = pool.filter(r => r.rarity === targetRarity);
  return filtered.length > 0 ? rng.pick(filtered) : rng.pick(pool);
}
